import React from 'react';
import KanbanCard from '../KanbanCard';
import { CheckCircle, XCircle, RefreshCw, ExternalLink } from 'lucide-react';

// Logos
import googleAdsLogo from '../../../logos/google-ads.png';
import microsoftAdsLogo from '../../../logos/microsoft-ads.png';
import metaAdsLogo from '../../../logos/meta-ads.png';
import pianoLogo from '../../../logos/piano-analytics.png';
import matomoLogo from '../../../logos/matomo.png';
import searchConsoleLogo from '../../../logos/google-search-console.png';
import tagManagerLogo from '../../../logos/google-tag-manager.png';
import merchantLogo from '../../../logos/google-merchant.png';
import googleAnalyticsLogo from '../../../logos/google-analytics.svg';
import pinterestAdsLogo from '../../../logos/pinterest-ads.png';
import tiktokAdsLogo from '../../../logos/tiktok-ads.png';
import chatgptAdsLogo from '../../../logos/chatgpt-ads.png';
import awinLogo from '../../../logos/awin.png';
import prestashopLogo from '../../../logos/prestashop.png';

// Config par provider avec logos
const providerConfig = {
  GOOGLE_ADS: {
    label: 'Google Ads',
    logo: googleAdsLogo
  },
  GOOGLE_SHEETS: {
    label: 'Google Sheets',
    logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-drive.png'
  },
  GOOGLE_MERCHANT_API: {
    label: 'Google Merchant Center',
    logo: merchantLogo
  },
  GOOGLE_ANALYTICS: {
    label: 'Google Analytics',
    logo: googleAnalyticsLogo
  },
  GOOGLE_SEARCH_CONSOLE: {
    label: 'Google Search Console',
    logo: searchConsoleLogo
  },
  GOOGLE_TAG_MANAGER: {
    label: 'Google Tag Manager',
    logo: tagManagerLogo
  },
  MICROSOFT_ADS: {
    label: 'Microsoft Ads',
    logo: microsoftAdsLogo
  },
  FACEBOOK_ADS: {
    label: 'Meta Ads',
    logo: metaAdsLogo
  },
  MATOMO: {
    label: 'Matomo',
    logo: matomoLogo
  },
  PIANO_ANALYTICS: {
    label: 'Piano Analytics',
    logo: pianoLogo
  },
  OPEN_METEO: {
    label: 'Open-Meteo',
    logo: null
  },
  PINTEREST_ADS: {
    label: 'Pinterest Ads',
    logo: pinterestAdsLogo
  },
  TIKTOK_ADS: {
    label: 'TikTok Ads',
    logo: tiktokAdsLogo
  },
  OPENAI_ADS: {
    label: 'ChatGPT Ads',
    logo: chatgptAdsLogo
  },
  PRESTASHOP: {
    label: 'PrestaShop',
    logo: prestashopLogo
  },
  AWIN: {
    label: 'Awin',
    logo: awinLogo
  }
};

const ConnectorCard = ({ connector, onConnect, onDisconnect, onRefresh, onClick, isSelected = false }) => {
  const config = providerConfig[connector.provider] || {
    label: connector.provider,
    logo: null
  };

  const isGroup = connector.isGroup;
  const isConnected = connector.isActive;

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.375rem'
  };

  const logoStyle = {
    width: '24px',
    height: '24px',
    objectFit: 'contain',
    flexShrink: 0
  };

  const providerLabelStyle = {
    fontSize: '0.75rem',
    fontWeight: '600',
    color: '#64748b'
  };

  const nameStyle = {
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '0.25rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textTransform: 'uppercase'
  };

  const emailStyle = {
    fontSize: '0.75rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };

  const statusRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '0.5rem'
  };

  const statusStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
    color: isConnected ? '#16a34a' : '#dc2626'
  };

  const actionsStyle = {
    display: 'flex',
    gap: '0.25rem'
  };

  const actionButtonStyle = {
    padding: '0.25rem',
    borderRadius: '0.25rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
  };

  const cardWrapperStyle = {
    marginBottom: '0.25rem'
  };

  // Affichage groupé (Matomo, Piano)
  if (isGroup) {
    return (
      <div style={cardWrapperStyle}>
        <KanbanCard
          id={connector.id}
          type="connector"
          data={connector}
          draggable={false}
          onClick={onClick}
          isSelected={isSelected}
        >
        <div>
          <div style={headerStyle}>
            {config.logo && (
              <img src={config.logo} alt={config.label} style={logoStyle} />
            )}
            <span style={providerLabelStyle}>{config.label}</span>
          </div>

          <div style={nameStyle}>
            {connector.count} connecteur{connector.count > 1 ? 's' : ''}
          </div>

          <div style={statusRowStyle}>
            <div style={statusStyle}>
              {isConnected ? (
                <>
                  <CheckCircle size={14} />
                  <span>Actif</span>
                </>
              ) : (
                <>
                  <XCircle size={14} />
                  <span>Inactif</span>
                </>
              )}
            </div>
          </div>
        </div>
      </KanbanCard>
      </div>
    );
  }

  // Affichage standard
  return (
    <div style={cardWrapperStyle}>
      <KanbanCard
        id={connector.id}
        type="connector"
        data={connector}
        draggable={false}
        onClick={onClick}
        isSelected={isSelected}
      >
      <div>
        <div style={headerStyle}>
          {config.logo && (
            <img src={config.logo} alt={config.label} style={logoStyle} />
          )}
          <span style={providerLabelStyle}>{config.label}</span>
        </div>

        <div style={emailStyle} title={connector.connectorEmail || connector.connectionName}>
          {connector.connectorEmail || connector.connectionName}
        </div>

        <div style={statusRowStyle}>
          <div style={statusStyle}>
            {isConnected ? (
              <>
                <CheckCircle size={14} />
                <span>Connecté</span>
              </>
            ) : (
              <>
                <XCircle size={14} />
                <span>Déconnecté</span>
              </>
            )}
          </div>

          <div style={actionsStyle}>
            {isConnected && onRefresh && (
              <button
                style={actionButtonStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefresh(connector);
                }}
                title="Rafraîchir"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3efe7';
                  e.currentTarget.style.color = '#1a1a1a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#64748b';
                }}
              >
                <RefreshCw size={14} />
              </button>
            )}
            <button
              style={actionButtonStyle}
              onClick={(e) => {
                e.stopPropagation();
                if (isConnected && onDisconnect) {
                  onDisconnect(connector);
                } else if (!isConnected && onConnect) {
                  onConnect(connector);
                }
              }}
              title={isConnected ? 'Gérer' : 'Connecter'}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3efe7';
                e.currentTarget.style.color = '#1a1a1a';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#64748b';
              }}
            >
              <ExternalLink size={14} />
            </button>
          </div>
        </div>
      </div>
    </KanbanCard>
    </div>
  );
};

export default ConnectorCard;
